/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { memo } from '@web-stories-wp/react';

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 5px;
  pointer-events: none;
  z-index: 2;
`;

const OverlayImage = styled.img`
  position: absolute;
  display: block;
  user-select: none;
  opacity: 1;
`;

const PIXEL_SNAP_EPSILON = 1e-6;

function isNearInteger(value) {
  return Math.abs(value - Math.round(value)) < PIXEL_SNAP_EPSILON;
}

function snapStart(value) {
  return isNearInteger(value) ? Math.round(value) : Math.floor(value);
}

function snapEnd(value) {
  return isNearInteger(value) ? Math.round(value) : Math.ceil(value);
}

function getOverlayStyle(zone, pageWidth, pageHeight) {
  const x = Math.max(zone.x ?? 0, 0);
  const y = Math.max(zone.y ?? 0, 0);
  const zoneWidth = Math.max(zone.width ?? 1, 0);
  const zoneHeight = Math.max(zone.height ?? 1, 0);

  // Snap start/end independently so fractional layout sizes never leave a
  // visible 1px gap at the bottom/right edge in FIT/FILL zoom modes.
  const left = snapStart(x * pageWidth);
  const top = snapStart(y * pageHeight);
  const right = snapEnd((x + zoneWidth) * pageWidth);
  const bottom = snapEnd((y + zoneHeight) * pageHeight);
  const width = Math.max(right - left, 0);
  const height = Math.max(bottom - top, 0);
  const style = {
    width: `${width}px`,
    height: `${height}px`,
    left: `${left}px`,
    top: `${top}px`,
  };
  const cornerRadius = Math.round(
    Math.max(zone.cornerRadius ?? 0, 0) * Math.min(pageWidth, pageHeight)
  );

  if (!cornerRadius) {
    return style;
  }

  return {
    ...style,
    borderRadius: `${cornerRadius}px`,
  };
}

function SafeZonesOverlay({ isVisible, pageWidth, pageHeight, safeZones }) {
  const overlayZones = safeZones.filter((zone) => zone?.id && zone?.imageSrc);

  if (!isVisible || !overlayZones.length || !pageWidth || !pageHeight) {
    return null;
  }

  return (
    <Overlay data-testid="safe-zones-overlay">
      {overlayZones.map((zone) => (
        <OverlayImage
          key={zone.id}
          src={zone.imageSrc}
          alt=""
          aria-hidden="true"
          data-testid={`safe-zone-${zone.id}`}
          style={getOverlayStyle(zone, pageWidth, pageHeight)}
        />
      ))}
    </Overlay>
  );
}

SafeZonesOverlay.propTypes = {
  isVisible: PropTypes.bool,
  pageWidth: PropTypes.number.isRequired,
  pageHeight: PropTypes.number.isRequired,
  safeZones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      cornerRadius: PropTypes.number,
    })
  ),
};

SafeZonesOverlay.defaultProps = {
  isVisible: false,
  safeZones: [],
};

export default memo(SafeZonesOverlay);
